import React, { useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Typography,
  Alert,
  Tooltip,
  Button,
  Stack,
  alpha,
  useTheme,
  CircularProgress,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { useQuery } from "react-query";
import { QK } from "../../base/qk";
import moment from "moment";
import { getWeeklyAssignmentByVisit } from "../../api/services/scheduling";
import AddEmployee from "./AddEmployee";

const ScheduleJobDetails = ({ visitId, area, area_name, isRunAdded }) => {
  const theme = useTheme();
  const [showModal, setShowModal] = useState(false);
  const [jobId, setJobId] = useState(null);

  const handleOpenModal = (id) => {
    setJobId(id);
    setShowModal(true);
  };

  const handleCloseModal = () => setShowModal(false);

  const { data: scheduleJobs, isLoading } = useQuery(
    [QK.weeklyAssignment, visitId],
    () => getWeeklyAssignmentByVisit(visitId),
    {
      enabled: !!visitId && isRunAdded,
    }
  );

  const getTimeRange = (start, end) =>
    `${moment(start, "HH:mm").format("HH:mm")} - ${moment(end, "HH:mm").format("HH:mm")}`;

  const getDuration = (start, end) => {
    const startTime = moment(start, "HH:mm");
    const endTime = moment(end, "HH:mm");

    if (endTime.isBefore(startTime)) {
      endTime.add(1, "day");
    }

    const duration = moment.duration(endTime.diff(startTime));
    const hours = Math.floor(duration.asHours());
    const minutes = duration.minutes();

    return `${hours}h ${minutes}m`;
  };

  const getDayColor = (day) => {
    const colors = {
      Monday: "#3B82F6",
      Tuesday: "#8B5CF6",
      Wednesday: "#EC4899",
      Thursday: "#F59E0B",
      Friday: "#10B981",
      Saturday: "#EF4444",
      Sunday: "#6B7280",
    };
    return colors[day] || "#6B7280";
  };

  if (!isRunAdded) {
    return (
      <Alert
        severity="warning"
        sx={{
          borderRadius: "8px",
          mt: 2,
          alignItems: "center",
        }}
      >
        <Typography variant="body2" fontWeight={500}>
          ⚠️ Please Add a Run For Assigning Employee
        </Typography>
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          overflow: "hidden",
          mt: 2,
        }}
      >
        <TableContainer>
          <Table size="medium">
            <TableHead>
              <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: "#475467",
                    fontSize: "0.75rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.3px",
                  }}
                >
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <CalendarTodayIcon sx={{ fontSize: 14, color: "#94A3B8" }} />
                    <span>Day</span>
                  </Stack>
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: "#475467",
                    fontSize: "0.75rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.3px",
                  }}
                >
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <AccessTimeIcon sx={{ fontSize: 14, color: "#94A3B8" }} />
                    <span>Time</span>
                  </Stack>
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: "#475467",
                    fontSize: "0.75rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.3px",
                  }}
                >
                  Duration
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: "#475467",
                    fontSize: "0.75rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.3px",
                  }}
                  align="center"
                >
                  Employees
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {scheduleJobs?.map((item) => (
                <TableRow
                  key={item.id}
                  hover
                  sx={{
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.02),
                    },
                  }}
                >
                  <TableCell>
                    <Chip
                      label={item.weekly_day}
                      size="small"
                      sx={{
                        bgcolor: alpha(getDayColor(item.weekly_day), 0.12),
                        color: getDayColor(item.weekly_day),
                        fontWeight: 600,
                        height: 24,
                        fontSize: "0.7rem",
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500} color="#1A1A2E">
                      {getTimeRange(item.time_required_from, item.time_required_to)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getDuration(item.time_required_from, item.time_required_to)}
                      size="small"
                      sx={{
                        bgcolor: "#F1F5F9",
                        color: "#475467",
                        height: 22,
                        fontSize: "0.65rem",
                        fontWeight: 500,
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center" flexWrap="wrap">
                      {item.employee_details?.map((emp, index) => (
                        <Chip
                          key={index}
                          label={emp.first_name}
                          size="small"
                          sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            color: theme.palette.primary.main,
                            height: 24,
                            fontSize: "0.65rem",
                            fontWeight: 500,
                            "&:hover": {
                              bgcolor: alpha(theme.palette.primary.main, 0.15),
                            },
                          }}
                        />
                      ))}
                      <Tooltip title="Add Employee" arrow>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenModal(item.id)}
                          sx={{
                            bgcolor: "#F1F5F9",
                            color: "#475467",
                            width: 28,
                            height: 28,
                            "&:hover": {
                              bgcolor: alpha(theme.palette.primary.main, 0.12),
                              color: theme.palette.primary.main,
                            },
                          }}
                        >
                          <PersonAddIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {(!scheduleJobs || scheduleJobs.length === 0) && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No schedule jobs found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <AddEmployee
        show={showModal}
        handleClose={handleCloseModal}
        area={area}
        jobId={jobId}
      />
    </Box>
  );
};

export default ScheduleJobDetails;